import { useState } from 'react';
import type { Course, Player, Round, ScoreFormat } from '../lib/types';
import type { Settings } from '../lib/settings';
import { outboxSize } from '../lib/api';
import { basemapProvider } from '../lib/basemap';
import { FEATURES } from '../lib/features.ts';
import { INDICATOR_LABELS, INDICATOR_DEFAULTS } from '../lib/indicators.ts';

interface Props {
  course: Course;
  courseLive: boolean;
  settings: Settings;
  onSettings: (patch: Partial<Settings>) => void;
  round: Round | null;
  onStartRound: (players: Player[], format: ScoreFormat) => void;
  onEndRound: () => void;
  gpsError: string | null;
}

const blank = (i: number, tee: string): Player => ({
  id: `p${i}-${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  handicap: 18,
  teeColour: tee,
});

export default function SetupScreen(props: Props) {
  const { course, courseLive, settings, onSettings, round, onStartRound, onEndRound, gpsError } = props;
  const [players, setPlayers] = useState<Player[]>([blank(1, settings.teeColour)]);
  const [format, setFormat] = useState<ScoreFormat>('stableford');

  const patch = (id: string, p: Partial<Player>) =>
    setPlayers((list) => list.map((x) => (x.id === id ? { ...x, ...p } : x)));

  const named = players.filter((p) => p.name.trim().length > 0);

  return (
    <div className="screen">
      {FEATURES.scoring && (round ? (
        <div className="card">
          <h2>ROUND IN PROGRESS</h2>
          <div className="row">
            <span className="pill ok">{round.courseName}</span>
            <span className="pill">{round.format.toUpperCase()}</span>
            <span className="pill">{round.players.map((p) => p.name).join(' · ')}</span>
            <div className="spacer" />
            <button className="btn danger" onClick={onEndRound}>
              End round
            </button>
          </div>
        </div>
      ) : (
        <div className="card">
          <h2>NEW ROUND</h2>
          {players.map((p, i) => (
            <div className="field" key={p.id}>
              <label>Player {i + 1}</label>
              <input
                value={p.name}
                placeholder="Name"
                onChange={(e) => patch(p.id, { name: e.target.value })}
              />
              <input
                style={{ maxWidth: 110 }}
                type="number"
                value={p.handicap}
                onChange={(e) => patch(p.id, { handicap: Number(e.target.value) })}
              />
              <select value={p.teeColour} onChange={(e) => patch(p.id, { teeColour: e.target.value })}>
                {course.teeColours.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              {players.length > 1 && (
                <button
                  className="btn ghost"
                  onClick={() => setPlayers((l) => l.filter((x) => x.id !== p.id))}
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <div className="row">
            <button
              className="btn"
              disabled={players.length >= 4}
              onClick={() => setPlayers((l) => [...l, blank(l.length + 1, settings.teeColour)])}
            >
              + Player
            </button>
            <select
              className="btn"
              value={format}
              onChange={(e) => setFormat(e.target.value as ScoreFormat)}
              style={{ background: 'var(--panel-2)' }}
            >
              <option value="stableford">Stableford</option>
              <option value="stroke">Stroke play</option>
              <option value="match">Match play</option>
            </select>
            <div className="spacer" />
            <button
              className="btn primary"
              disabled={!named.length}
              onClick={() => onStartRound(named, format)}
            >
              Start round
            </button>
          </div>
        </div>
      ))}

      <div className="card">
        <h2>UNIT</h2>
        <div className="field">
          <label>Buggy ID</label>
          <input
            value={settings.buggyId}
            onChange={(e) => onSettings({ buggyId: e.target.value.toUpperCase() })}
          />
        </div>
        <div className="field">
          <label>Mode</label>
          <select value={settings.role} onChange={(e) => onSettings({ role: e.target.value as 'buggy' | 'shop' })}>
            <option value="buggy">Buggy head unit</option>
            <option value="shop">Pro shop / marshal</option>
          </select>
        </div>
        <div className="field">
          <label>Theme</label>
          <select
            value={settings.theme}
            onChange={(e) => onSettings({ theme: e.target.value as Settings['theme'] })}
          >
            <option value="dark">Dark</option>
            <option value="light">Light</option>
          </select>
        </div>
        <div className="field">
          <label>Map</label>
          <select
            value={settings.mapMode}
            onChange={(e) => onSettings({ mapMode: e.target.value as Settings['mapMode'] })}
          >
            <option value="poly">Golf poly (no imagery)</option>
            <option value="satellite">Golf satellite</option>
            <option value="hybrid">Golf hybrid</option>
            <option value="road">Road</option>
          </select>
        </div>
        <div className="field">
          <label>Distances</label>
          <select value={settings.unit} onChange={(e) => onSettings({ unit: e.target.value as 'm' | 'yd' })}>
            <option value="m">Metres</option>
            <option value="yd">Yards</option>
          </select>
        </div>
        <div className="field">
          <label>Default tee</label>
          <select value={settings.teeColour} onChange={(e) => onSettings({ teeColour: e.target.value })}>
            {course.teeColours.map((c) => (
              <option key={c}>{c}</option>
            ))}
          </select>
        </div>
        <div className="row">
          <button
            className={`btn ${settings.keepAwake ? 'primary' : 'ghost'}`}
            onClick={() => onSettings({ keepAwake: !settings.keepAwake })}
          >
            Keep screen awake
          </button>
          <button
            className={`btn ${settings.bigText ? 'primary' : 'ghost'}`}
            onClick={() => onSettings({ bigText: !settings.bigText })}
          >
            Large text
          </button>
          <button
            className={`btn ${settings.demoDrive ? 'primary' : 'ghost'}`}
            onClick={() => onSettings({ demoDrive: !settings.demoDrive })}
          >
            Demo drive
          </button>
          <button
            className={`btn ${settings.lockToHole ? 'primary' : 'ghost'}`}
            onClick={() => onSettings({ lockToHole: !settings.lockToHole })}
          >
            Pen map to hole
          </button>
          <button
            className={`btn ${settings.courseMode ? 'primary' : 'ghost'}`}
            onClick={() => onSettings({ courseMode: !settings.courseMode })}
          >
            Course mode
          </button>
          <button
            className={`btn ${settings.cartPathMode ? 'primary' : 'ghost'}`}
            onClick={() => onSettings({ cartPathMode: !settings.cartPathMode })}
          >
            Cart paths
          </button>
        </div>
      </div>

      {/* INDICATORS — what the map draws. Lives here rather than on the map
          because the car screen takes no touch: whatever is set on the phone is
          what every surface, CarPlay included, will draw. */}
      <div className="card">
        <h2>INDICATORS</h2>
        <div className="muted" style={{ marginBottom: 8 }}>
          What the map draws. Applies to this screen, the buggy screen and CarPlay.
        </div>
        <div className="indicators">
          {INDICATOR_LABELS.map(({ key, label, hint }) => {
            const on = (settings.indicators ?? INDICATOR_DEFAULTS)[key] !== false;
            return (
              <button
                key={key}
                className={`ind ${on ? 'on' : ''}`}
                onClick={() =>
                  onSettings({
                    indicators: { ...(settings.indicators ?? INDICATOR_DEFAULTS), [key]: !on },
                  })
                }
              >
                <span className="ind-dot" aria-hidden />
                <span className="ind-copy">
                  <b>{label}</b>
                  <span>{hint}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div className="row" style={{ marginTop: 10 }}>
          <button className="btn" onClick={() => onSettings({ indicators: INDICATOR_DEFAULTS })}>
            All on
          </button>
        </div>
      </div>

      <div className="card">
        <h2>DIAGNOSTICS</h2>
        <table className="grid">
          <tbody>
            <tr>
              <td className="name">Course</td>
              <td>{course.name}</td>
              <td>{courseLive ? 'live from server' : 'cached / built-in'}</td>
            </tr>
            <tr>
              <td className="name">Holes</td>
              <td>{course.holes.length}</td>
              <td>{course.keepOut.length} fenced zones</td>
            </tr>
            <tr>
              <td className="name">Basemap</td>
              <td>{basemapProvider}</td>
              <td>{basemapProvider === 'esri' ? 'evaluation imagery' : 'licensed'}</td>
            </tr>
            <tr>
              <td className="name">GPS</td>
              <td colSpan={2} style={{ color: gpsError ? 'var(--danger)' : 'var(--accent)' }}>
                {gpsError ?? 'receiving'}
              </td>
            </tr>
            <tr>
              <td className="name">Map</td>
              <td colSpan={2}>
                {settings.lockToHole
                  ? 'penned to the hole — a round loads a few hundred tiles'
                  : 'free roam — panning will stream tiles for anywhere you go'}
              </td>
            </tr>
            <tr>
              <td className="name">Unsent</td>
              <td colSpan={2}>{outboxSize()} queued writes</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
